import { IconButton } from "@carbon6/c6-frontend-design";
import { useMemo, type ReactNode } from "react";
import { triggerBrowserDownload } from "@/utils";
import { buildSummaryCsv, summaryCsvFilename } from "./evalCompareCsvModel";
import { isPooledSeriesKey, MERGED_SERIES_KEY, type EvalResultSeries } from "./evalCompareModel";
import { bestStatValue, summaryStatGroups, type CompareStatGroup } from "./evalCompareSummaryModel";
import { CompareTable, resultSeriesColumns, type CompareTableRow } from "./CompareTable";
import tableStyles from "./CompareTable.module.css";
import styles from "./EvaluationComparisonViewer.module.css";

// 최고값은 같은 층의 열끼리만 비교한다. 통합 창은 통합 결과와 그 입력인 run 을 함께 표시한다.
// Seed 하나짜리 run 이 pooled 결과보다 높게 표시되면 비교가 성립하지 않는다. 그래서 병합 열이
// 있으면 baseline 과 통합 결과만 비교한다. 병합 열이 없는 비교 창은 baseline 과 run 을 비교한다.
type BestValueRule = {
  candidates: ReadonlySet<EvalResultSeries>;
  footnote: string;
};

function bestValueRule(items: readonly EvalResultSeries[]): BestValueRule {
  const pooledOnly = items.some((item) => item.key === MERGED_SERIES_KEY);
  return {
    candidates: new Set(pooledOnly ? items.filter((item) => isPooledSeriesKey(item.key)) : items),
    footnote: pooledOnly
      ? "* Seed 를 합친 열끼리 비교해 행에서 가장 좋은 값을 표시하되, Baseline 과 통합 결과만 비교해서 표현합니다."
      : "* 행에서 가장 좋은 값",
  };
}

function tableRows(
  items: readonly EvalResultSeries[],
  groups: readonly CompareStatGroup[],
  candidates: ReadonlySet<EvalResultSeries>,
): CompareTableRow[] {
  const candidateList = [...candidates];
  return groups.flatMap((group) =>
    group.columns.map((column) => {
      const best = bestStatValue(candidateList, column);
      return {
        key: column.key,
        group: group.header ?? column.label,
        groupKey: group.key,
        label: group.header !== null ? column.label : "",
        labelMuted: true,
        cells: items.map((item) => {
          const value = column.value(item);
          const isBest =
            value !== null &&
            best !== null &&
            value === best &&
            candidates.size > 1 &&
            candidates.has(item);
          return {
            value: value === null ? null : `${isBest ? "* " : ""}${column.format(value)}`,
            highlight: isBest,
            title: isBest ? "행에서 가장 좋은 값" : undefined,
            missingLabel: "N/A",
          };
        }),
      };
    }),
  );
}

function downloadTextFile(text: string, filename: string, mime: string): void {
  const href = URL.createObjectURL(new Blob([text], { type: mime }));
  try {
    triggerBrowserDownload(href, { filename });
  } finally {
    URL.revokeObjectURL(href);
  }
}

export function CompareSummaryTable({
  items,
  onOpenRun,
  includeSampleCount = false,
  exportFilename,
  exportAppendix = "",
}: {
  items: readonly EvalResultSeries[];
  onOpenRun: (runId: string) => void;
  includeSampleCount?: boolean;
  exportFilename?: string;
  exportAppendix?: string;
}): ReactNode {
  const groups = useMemo(
    () => summaryStatGroups(items, includeSampleCount),
    [items, includeSampleCount],
  );
  const rule = useMemo(() => bestValueRule(items), [items]);
  const rows = useMemo(() => tableRows(items, groups, rule.candidates), [items, groups, rule]);
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>요약</h3>
        <IconButton
          type="button"
          variant="surface"
          size="sm"
          text="CSV 내보내기"
          title="요약 표를 CSV 로 내보냅니다."
          aria-label="요약 표를 CSV 로 내보냅니다."
          onClick={() =>
            downloadTextFile(
              `${buildSummaryCsv(items, groups)}${exportAppendix}`,
              exportFilename ?? summaryCsvFilename(items.length),
              "text/csv;charset=utf-8",
            )
          }
        />
      </div>
      <CompareTable
        groupHeader="지표"
        leafHeader="통계"
        columns={resultSeriesColumns(items, onOpenRun)}
        rows={rows}
        footer={
          rule.candidates.size > 1 ? <p className={tableStyles.footnote}>{rule.footnote}</p> : null
        }
      />
    </section>
  );
}
